/**
 * English strings — the source catalogue. Every user-visible string in the app lives
 * here. Add a language by copying hi.ts; TypeScript enforces that every key is
 * translated. Placeholders use {name}.
 */
export const en = {
  app: {
    name: 'Tool Tracker',
    product: 'SaarthiOS PACE',
    tagline: 'Plan vs actual for tool development',
  },
  common: {
    of: 'of',
    back: 'Back',
    cancel: 'Cancel',
    save: 'Save',
    close: 'Close',
    confirm: 'Confirm',
    undo: 'Undo',
    undone: 'Undone',
    retry: 'Try again',
    loading: 'Loading…',
    change: 'Change',
    edit: 'Edit',
    add: 'Add',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    today: 'Today',
    yesterday: 'Yesterday',
    daysAgo: '{n} days ago',
    ext: 'Ext {ext}',
    by: 'by {name}',
    delete: 'Delete',
    show: 'Show',
    none: '—',
    optional: 'optional',
    note: 'Note',
    photo: 'Photo',
    photos: 'Photos',
    addPhoto: 'Add photo',
    download: 'Download',
    days: '{n} days',
    workingDays: '{n} working days',
    search: 'Search',
    details: 'Details',
    somethingWrong: 'Something went wrong. Please try again.',
    notFound: 'Page not found',
    goHome: 'Go to my work',
    notAllowed: 'This screen is not part of your role.',
    offlineRead: 'Offline — showing the last saved data.',
  },
  roles: {
    owner: 'Stage owner',
    planner: 'Planner',
    mgmt: 'Management',
    admin: 'Admin',
  },
  /** Step status (computed): colour + icon + word everywhere. */
  status: {
    DONE: 'Done',
    DONE_LATE: 'Done late',
    OVERDUE: 'Overdue',
    DUE_TODAY: 'Due today',
    AT_RISK: 'At risk',
    UPCOMING: 'Upcoming',
    HOLD: 'On hold',
    SKIPPED: 'N/A',
  },
  pstatus: {
    ON_TRACK: 'On track',
    AT_RISK: 'At risk',
    LATE: 'Late',
    SHIPPED: 'Shipped',
    DRAFT: 'Draft',
    CANCELLED: 'Cancelled',
  },
  slip: {
    late: '+{n}d',
    early: '−{n}d',
    onTime: 'On time',
    lateLong: '{n} days late',
    earlyLong: '{n} days early',
  },
  login: {
    tapYourName: 'Tap your name',
    enterPin: 'Enter your 4-digit PIN',
    wrongPin: 'Wrong PIN. {n} tries left before the account locks for 15 minutes.',
    wrongPinShort: 'Wrong PIN. Try again.',
    locked: 'Too many wrong PINs. Locked until {time}. Ask the Admin to unlock.',
    notYou: 'Not you?',
    firstTime: 'First time here? Set up your PIN',
    noPin: 'You have not set a PIN yet.',
    setUpNow: 'Set up now',
    sharedDevice: '{label} · shared {role} device',
    pairTitle: 'Set up as a shared tool-room device',
    pairHint: 'Enter the 6-digit code from Admin → Devices.',
    pairButton: 'Pair this device',
    pairDone: 'This device is now a shared {role} device.',
    demoPin: 'Demo PIN for every user: {pin}',
    loginFailed: 'Could not log in. Check the connection and try again.',
    welcomeBack: 'Welcome back',
  },
  setup: {
    title: 'First-time setup',
    mobile: 'Your mobile number',
    mobileHint: 'The number the Admin registered for you.',
    sendOtp: 'Send code',
    otpSentTo: 'We sent a 6-digit code to {mobile}.',
    enterOtp: 'Enter the 6-digit code',
    demoOtp: 'Demo code: {code}',
    verify: 'Verify',
    choosePin: 'Choose a 4-digit PIN',
    confirmPin: 'Enter the same PIN again',
    pinsDontMatch: 'The PINs do not match. Start again.',
    weakPin: 'Too easy to guess. Pick another PIN.',
    done: 'PIN set. You are logged in.',
    wrongOtp: 'Wrong code. Check the SMS and try again.',
    expired: 'That code expired. Ask for a new one.',
    resend: 'Send a new code',
    step: 'Step {n} of 3',
  },
  shell: {
    synced: 'Synced',
    syncing: 'Syncing…',
    pending: '{n} to sync',
    offline: 'Offline',
    offlineQueued: 'Offline — your taps are saved and will sync.',
    waitingOnYou: '{n} waiting',
    logout: 'Log out',
    language: 'Language',
    workingAs: 'Working as',
    profile: 'Profile',
    sessionEnds: 'Session ends at {time}',
    sharedIdle: 'Logs out after 15 minutes without use',
    me: 'Me',
    views: 'Views',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
    sync: 'Sync',
    allSaved: 'All changes saved · {time}',
    updateReady: 'A new version is ready.',
    reload: 'Reload',
    liveOff: 'Live updates paused — reconnecting…',
    sessionEnded: 'Your session ended. Enter your PIN again.',
    theme: 'Theme',
    themeAuto: 'Auto',
    themeLight: 'Light',
    themeDark: 'Dark',
    version: 'Version {v}',
    actions: 'Actions',
    closeActions: 'Close actions',
  },
  work: {
    title: 'My work',
    todo: 'To do',
    coming: 'Coming up',
    done: 'Done',
    overdue: 'Overdue',
    dueToday: 'Due today',
    thisWeek: 'Next 7 days',
    later: 'Later',
    onHold: 'On hold',
    waitingFor: 'After {stage}',
    waitingPrev: 'Waiting for the stage before',
    doneToday: 'Done today',
    doneOn: 'Done on…',
    hold: 'Hold',
    resume: 'Resume',
    notDone: 'Not done yet',
    changeDate: 'Change date',
    planned: 'Plan {date}',
    forecast: 'Now expected {date}',
    doneDate: 'Done {date}',
    allClear: 'All clear',
    noTodo: 'Nothing due in your stages this week. New work shows up here when a project is published.',
    noComing: 'Nothing else planned for your stages yet.',
    noDone: 'Nothing finished in the last 30 days.',
    noDepartments: 'You are not assigned to a department yet. Ask the Admin to add yours.',
    savedDone: '{stage} · {tool}: done {date}',
    savedHold: '{stage} · {tool}: on hold ({reason})',
    savedResume: '{stage} · {tool}: resumed',
    savedReopen: '{stage} · {tool}: marked not done',
    locked: 'The next stage has started. Ask the planner ({name}) to change this.',
    whenDone: 'When was it done?',
    pickDate: 'Pick a date',
    noteOptional: 'Note (optional)',
    notePlaceholder: 'e.g. sent to vendor, rework on punch',
    holdTitle: 'Why is it on hold?',
    oneTapSaves: 'One tap on a reason saves.',
    photoAdded: 'Photo added',
    photoNeedsNet: 'Photos need a connection. Try again when online.',
    myDepartments: 'My stages',
    keyboardHint: '↑ ↓ move · D done today · H hold',
    daysLeft: 'in {n} days',
    tomorrow: 'tomorrow',
    lateBy: '{n} days late',
  },
  projects: {
    title: 'Projects',
    new: 'New project',
    import: 'Import Excel',
    empty: 'No projects yet',
    emptyHint: 'Create a project, or import an existing tracker sheet.',
    noMatch: 'No projects match.',
    tools: '{n} tools',
    progress: '{done} of {total} stages done',
    ends: 'Ends {date}',
    endsPlan: 'Plan {plan} · Forecast {forecast}',
    drafts: 'Drafts',
    closed: 'Shipped & closed',
    showClosed: 'Show shipped and cancelled',
    partName: 'Part name',
    partNo: 'Part no.',
    customer: 'Customer',
    startDate: 'Start date',
    targetDate: 'Promised to customer',
    targetHint: 'Optional — shown next to the forecast.',
    notes: 'Notes',
    toolsTitle: 'Tools',
    toolsHint:
      'Pick the tools this part needs. Durations come from the tool type; you can change any date afterwards.',
    addTool: 'Add tool',
    toolName: 'Tool name',
    toolNo: 'Tool no.',
    toolType: 'Tool type',
    noType: 'Other (stage defaults)',
    create: 'Create draft',
    created: 'Draft created. Check the dates, then publish.',
  },
  project: {
    tracker: 'Tracker',
    timeline: 'Timeline',
    history: 'History',
    photos: 'Photos',
    milestones: 'Trials & shipping',
    plan: 'Plan',
    actual: 'Actual',
    forecast: 'Forecast',
    baseline: 'Baseline',
    baselineV: 'Baseline v{v}',
    stage: 'Stage',
    tool: 'Tool',
    end: 'Tool shipping',
    endPlan: 'Plan end',
    endForecast: 'Forecast end',
    target: 'Promised {date}',
    draftNote:
      'Draft — only planners see it. Publish to freeze the plan as the baseline and put the work on everyone’s list.',
    cancelledNote: 'Cancelled. Nothing here shows on work lists.',
    shippedNote: 'Closed after shipping.',
    publish: 'Publish plan',
    publishConfirm: 'Tap again to publish and freeze the baseline',
    published: 'Published. The plan is now the baseline and on everyone’s work list.',
    rebaseline: 'Re-baseline',
    rebaselineTitle: 'Make the current plan the new baseline',
    rebaselineHint: 'Use this when the customer agrees a new date. The old baseline stays in the history.',
    rebaselineReason: 'Reason',
    rebaselined: 'New baseline v{v} saved.',
    planChanged: 'Plan differs from the baseline in {n} places.',
    addTool: 'Add tool',
    addRound: 'Add trial round',
    roundLabel: 'Name (e.g. T3)',
    roundAfter: 'After',
    removeTool: 'Remove tool',
    removeToolConfirm: 'Tap again to remove {name}',
    removeStep: 'Remove',
    export: 'Export Excel',
    editDetails: 'Edit details',
    cancelProject: 'Cancel project',
    cancelConfirm: 'Tap again to cancel {name}',
    reopen: 'Reopen',
    closeProject: 'Close as shipped',
    deleteDraft: 'Delete draft',
    deleteConfirm: 'Tap again to delete this draft',
    deleted: 'Draft deleted.',
    regenerate: 'Recalculate dates',
    regenerateHint: 'Every open stage gets a date from its duration, starting from the start date.',
    editDate: 'Plan date',
    moveLater: 'Move the later stages too (keep the gaps)',
    notNeeded: 'Not needed for this tool (N/A)',
    duration: 'Duration',
    durationHint: 'Working days after the stage before.',
    owner: 'Owner',
    noOwner: 'No owner yet',
    stepFor: '{stage} · {tool}',
    issues: {
      missing_date: 'Some stages have no plan date.',
      before_previous: '{stage} ({tool}) is planned before the stage before it.',
      before_tools: '{stage} is planned before every tool is finished.',
      before_start: '{stage} ({tool}) is planned before the project start.',
    },
    photosEmpty: 'No photos yet. Stage owners add them when they mark work done.',
    historyEmpty: 'Nothing has happened yet.',
    legend: 'Plan (baseline) above, actual or forecast below.',
    today: 'Today',
    open: 'Open project',
  },
  timeline: {
    done: 'Done ({date})',
    hold: 'On hold: {reason}',
    release: 'Resumed',
    reopen: 'Marked not done',
    note: 'Note: {note}',
    photo: 'Photo added',
    'plan.edit': 'Plan changed',
    'plan.generate': 'Dates recalculated',
    'project.create': 'Project created',
    'project.import': 'Imported from Excel',
    'project.publish': 'Published — baseline v1',
    'project.rebaseline': 'Re-baselined',
    'project.cancelled': 'Cancelled',
    'project.active': 'Reopened',
    'project.shipped': 'Closed as shipped',
    'tool.add': 'Tool added: {name}',
    'tool.remove': 'Tool removed: {name}',
    'milestone.add': 'Trial round added',
    'milestone.remove': 'Trial round removed',
    notify: 'Owner notified by email',
    undone: 'undone',
  },
  notify: {
    button: 'Notify',
    to: 'Notify {name}',
    note: 'Message for the notification',
    notePlaceholder: 'Add a message (optional)',
    sent: 'Email sent to {names}.',
    dummy: '(Demo: emails are logged, not delivered yet.)',
    recently: 'You already notified them in the last 5 minutes.',
    nobody: 'Nobody owns this stage yet. Ask the Admin to assign a department.',
    offline: 'Notifying needs a connection. Try again when online.',
  },
  import: {
    title: 'Import a tracker sheet',
    hint: 'The same layout as your Excel timeplan: Part Name, Operation and Status columns, then one column per stage, with a Plan and an Actual row for each tool.',
    pick: 'Choose .xlsx file',
    reading: 'Reading the sheet…',
    columns: '{n} of {total} columns matched to stages',
    unmatched: 'Not matched (ignored): {list}',
    tools: 'Tools found',
    stagesDone: '{n} stages done',
    formula:
      '{n} “actual” dates are only copies of the plan (formulas). Nobody typed them, so they stay not done.',
    acceptFormula: 'Treat them as done anyway',
    future: '{n} actual dates are in the future and were left out.',
    before_tools:
      'The sheet plans {detail} before every tool is finished. You can fix the dates after importing.',
    before_previous: 'A date runs backwards: {detail}.',
    multiple: 'Only the first part was imported. Also in the file: {detail}.',
    noDates: 'No dates found in the sheet.',
    create: 'Create draft project',
    created: 'Imported as a draft. Check the plan and publish it.',
    another: 'Choose another file',
  },
  dashboard: {
    title: 'Dashboard',
    projects: 'Projects',
    attention: 'Needs attention',
    attentionHint: 'Overdue, on hold and due today — oldest first.',
    noAttention: 'Nothing overdue or on hold. 👍',
    week: 'Due in the next 7 days',
    noWeek: 'Nothing due in the next 7 days.',
    upcoming: 'Trials & shipping — next 14 days',
    noUpcoming: 'No trials or shipping in the next 14 days.',
    bottleneck: 'Where time is lost',
    bottleneckHint: 'Open overdue stages and average delay of finished ones, last 60 days.',
    overdueN: '{n} overdue',
    holdN: '{n} on hold',
    avgLate: 'avg {n}d late',
    reports: 'Reports',
    all: 'All projects',
    stagesOverdue: 'Stages overdue',
    stagesHold: 'On hold',
    dueToday: 'Due today',
  },
  display: {
    name: 'Floor Display',
    cta: 'Put every project on the tool-room TV.',
    open: 'Open',
    exit: 'Exit',
    asOn: 'As on {date} · {time}',
    theme: 'Switch light / dark',
  },
  reports: {
    title: 'Reports',
    range: 'Period',
    last30: 'Last 30 days',
    last90: 'Last 90 days',
    thisMonth: 'This month',
    from: 'From',
    to: 'To',
    statusXlsx: 'Status report (Excel)',
    statusPdf: 'Status report (PDF)',
    downloadHint: 'Every live project, and every stage that is overdue, at risk or on hold.',
    stagesTitle: 'On time by stage',
    deptTitle: 'On time by department',
    holdsTitle: 'Hold reasons',
    shippedTitle: 'Shipped in this period',
    noShipped: 'Nothing shipped in this period.',
    noHolds: 'No holds in this period.',
    noDone: 'No stages finished in this period.',
    colStage: 'Stage',
    colDept: 'Department',
    colDone: 'Done',
    colOnTime: 'On time',
    colSlip: 'Avg delay',
    colDays: 'Took / planned',
    colCount: 'Times',
    colDate: 'Date',
    colVsPlan: 'Vs plan',
  },
  admin: {
    title: 'Setup',
    subtitle: 'People, stages, tool types and devices for {plant}',
    users: 'People',
    departments: 'Departments',
    stages: 'Stages',
    toolTypes: 'Tool types',
    reasons: 'Hold reasons',
    calendar: 'Plant & calendar',
    devices: 'Shared devices',
    audit: 'Audit log',
    addUser: 'Add person',
    editUser: 'Edit person',
    name: 'Name',
    mobile: 'Mobile',
    titleField: 'Job title',
    departmentsField: 'Departments (stages they update)',
    departmentsHint: 'Stage owners see and update only these departments’ stages.',
    extField: 'Extension',
    rolesField: 'Roles',
    languageField: 'Language',
    initialPin: 'Set a PIN now (for plant-issued phones)',
    initialPinHint: 'Leave empty and the person sets their own PIN with an OTP.',
    resetPin: 'Reset PIN',
    resetPinConfirm: 'Tap again: {name} sets a new PIN at next login',
    unlock: 'Unlock',
    lockedUntil: 'Locked until {time}',
    noPinYet: 'PIN not set',
    inactive: 'Inactive',
    active: 'Active',
    uploadPhoto: 'Upload photo',
    saved: 'Saved',
    addDepartment: 'Add department',
    nameEn: 'Name (English)',
    nameHi: 'Name (Hindi)',
    addStage: 'Add stage',
    toolStages: 'Tool stages',
    toolStagesHint: 'The columns every tool goes through, in order.',
    projectStages: 'Trials & shipping',
    projectStagesHint: 'Project milestones after all tools are ready, in order.',
    code: 'Short name',
    codeHint: 'Column header, e.g. VMC or T0.',
    department: 'Owner department',
    noDepartment: '— Planner only —',
    defaultDays: 'Default duration (working days)',
    moveUp: 'Move up',
    moveDown: 'Move down',
    addToolType: 'Add tool type',
    toolTypeName: 'Tool type',
    daysFor: 'Working days per stage',
    daysHint: 'Empty = stage default · N/A = this kind of tool skips the stage.',
    na: 'N/A',
    addReason: 'Add reason',
    reasonEn: 'Label (English)',
    reasonHi: 'Label (Hindi)',
    icon: 'Icon',
    maxReasons: 'At most 8 active.',
    plantName: 'Plant name',
    timezone: 'Timezone',
    weeklyOff: 'Weekly off',
    weeklyOffHint: 'Plan durations skip these days. Turn all off to count every day, like the old sheet.',
    holidays: 'Holidays',
    holidaysHint: 'Dates the tool room is closed.',
    addHoliday: 'Add holiday',
    addDevice: 'Add shared device',
    deviceLabel: 'Device name',
    deviceRole: 'Who uses it',
    pairingCode: 'Pairing code: {code}',
    pairingHint:
      'On the device, open the login screen → “Set up as a shared tool-room device” and enter this code. Valid 24 h.',
    newCode: 'New code',
    paired: 'Paired {time}',
    notPaired: 'Not paired yet',
    lastSeen: 'Last seen {time}',
    unpair: 'Unpair',
    auditWhen: 'When',
    auditWho: 'Who',
    auditWhat: 'Action',
    auditEntity: 'Record',
    loadMore: 'Load more',
  },
  weekday: {
    '0': 'Sun',
    '1': 'Mon',
    '2': 'Tue',
    '3': 'Wed',
    '4': 'Thu',
    '5': 'Fri',
    '6': 'Sat',
  },
  errors: {
    forbidden: 'Your role or department cannot do that.',
    not_found: 'That no longer exists.',
    conflict: 'Someone else changed this. The screen is updated.',
    not_active: 'This project is not live (draft or closed).',
    skipped: 'This stage is not needed (N/A) for this tool.',
    bad_date: 'That date is not valid.',
    future_date: 'A done date cannot be in the future.',
    before_start: 'That date is long before the project started.',
    locked: 'The next stage has already acted. Ask the planner to change it.',
    not_done: 'It is not marked done.',
    not_on_hold: 'It is not on hold.',
    reason_invalid: 'That reason was removed. Pick another.',
    undo_expired: 'Too late to undo. Use Change instead.',
    undo_not_allowed: 'This can no longer be undone.',
    invalid: 'Could not save. Please try again.',
    network: 'No connection. Saved on this device; it will sync.',
    rejected: 'Not saved: {reason}',
  },
  welcome: {
    title: 'Set up your plant',
    intro: 'A fresh PACE server. Create the plant and the first Admin — it takes a minute.',
    step: 'Step {n} of 3',
    codeTitle: 'Setup code',
    codeHint: 'The 6-digit code shared with you by the team.',
    codeWrong: 'That setup code is not right. Check the code the team shared with you.',
    plantTitle: 'Plant',
    plantName: 'Plant name',
    plantNameHint: 'As people call it, e.g. “Faridabad Tool Room”.',
    timezone: 'Timezone',
    weeklyOff: 'Weekly off',
    starterSetup:
      'Start with the standard tool-room stages (Design → Trial, T0 → Shipping), departments and tool types',
    adminTitle: 'First Admin',
    adminName: 'Admin name',
    adminMobile: 'Admin mobile number',
    adminMobileHint: 'The Admin logs in with this number, a one-time code and a 4-digit PIN.',
    adminJobTitle: 'Job title',
    create: 'Create plant and Admin',
    next: 'Next',
    back: 'Back',
    done: 'Plant created. Now activate the Admin account.',
    otpDefault: 'OTP is the default code {code} for now (no SMS yet).',
    already: 'This plant is already set up.',
  },
  onboarding: {
    title: 'Getting started',
    intro: 'Finish these once and the tool room can start using PACE.',
    stages: 'Check the stages and who owns each',
    stagesHint: 'The columns of your tracker sheet, with the department that updates each.',
    toolTypes: 'Check tool types and their durations',
    users: 'Add people: planner, stage owners, management',
    usersHint: 'Give each stage owner their departments.',
    calendar: 'Set the weekly off and holidays',
    devices: 'Pair a shared tool-room phone (optional)',
    project: 'The planner creates the first project — or imports a tracker sheet',
    activate: 'Each person opens the app → “First time here?” → mobile → OTP {code} → PIN.',
    go: 'Open',
    hide: 'Hide',
    userAdded: '{name} added. They open the app → First time here → their mobile → OTP → choose a PIN.',
  },
  demo: {
    badge: 'Demo',
    banner: 'Demo data — a sample tool room with PIN 1234 for everyone. Nothing here is real.',
    tryIt: 'Try the demo with sample data →',
    reset: 'Reset demo data',
    realApp: 'Go to the real app',
    resetDone: 'Demo reset to the sample tool room.',
  },
  landing: {
    login: 'Log in',
    tryDemo: 'Try the demo',
    headline: 'Every tool, every stage, plan vs actual — without chasing anyone.',
    lede: 'Each stage owner enters the date their work was done, in one tap on the phone. The plan stays frozen, the forecast moves on its own, and everyone sees which tool will be late — weeks before the customer trial.',
    heroNote: 'Phone or laptop browser · nothing to install · English and हिंदी',
    howTitle: 'Your tracker sheet, alive',
    howLede:
      'The same Plan and Actual rows your team already fills in — now updated by the people doing the work.',
    stepPlan: 'Plan',
    stepPlanText:
      'The planner picks the tools; dates come from the stage durations. Publish freezes the baseline.',
    stepActual: 'Actual',
    stepActualText:
      'Design, VMC, heat treatment, wire cut, assembly and trial each tap Done — today or back-dated.',
    stepForecast: 'Forecast',
    stepForecastText:
      'A slip anywhere moves every later date, down to T0 and shipping. Nobody retypes a formula.',
    stepAlert: 'Attention',
    stepAlertText: 'Overdue, on hold and at risk surface on the dashboard, with one tap to notify the owner.',
    rolesTitle: 'One screen per role',
    rolesLede: 'Everyone lands on their own work straight after the PIN. No menus to learn.',
    roleOwner:
      'Stage owners: what is due in your stages this week. Tap Done, pick a date, or Hold with a reason. Add a photo.',
    rolePlanner:
      'Planner: create a project from tool types, adjust dates in a sheet-like grid, publish. Import old trackers.',
    roleMgmt: 'Management: every project on track, at risk or late, where time is lost, and weekly reports.',
    roleAdmin:
      'Admin: people and their departments, stages, tool types, holidays — and the Floor Display for the TV.',
    floorTitle: 'Built for the tool room',
    f1: 'One tap',
    f1Text: 'Done today is a single tap. Back-dating is two. No typing dates on the shop floor.',
    f2: 'Works offline',
    f2Text: 'Taps are saved on the phone during a network drop and sync on their own.',
    f3: 'Baseline stays honest',
    f3Text: 'The plan you committed is frozen. Re-baselining needs a reason and keeps the history.',
    f4: 'Readable at a glance',
    f4Text: 'Colour, icon and word on every status, big targets, Hindi and English.',
    f5: 'Excel in, Excel out',
    f5Text: 'Import your existing timeplan sheets; export any project in the same layout.',
    f6: 'Secure by design',
    f6Text: 'PIN login, lockout after 5 tries, every change audited, data hosted in India.',
    startTitle: 'Getting a tool room started',
    s1: 'Set up the plant',
    s1Text: 'Stages, departments and tool types come ready from the standard tool-room flow.',
    s2: 'Add people',
    s2Text: 'Admin adds each person with their mobile, role and departments.',
    s3: 'Everyone activates',
    s3Text: 'Mobile → one-time code → a 4-digit PIN. From then on, just the PIN.',
    ctaTitle: 'See it with a sample tool room',
    ctaText:
      'The demo has five press-tool projects — including the Heater Deflector tracker — laid out around today. Every sample user’s PIN is 1234.',
    footer: 'SaarthiOS PACE · AI Gurukul Pvt Ltd',
    previewTitle: 'Heater Deflector · 6 tools',
    previewPlan: 'Plan',
    previewForecast: 'Forecast',
    previewLate: '+5 days',
  },
  a11y: {
    delete: 'Delete digit',
    menu: 'Open profile',
    close: 'Close',
    skip: 'Skip to content',
  },
} as const;

type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };
export type Messages = Widen<typeof en>;
